import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Stack,
    Grid,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import useSEO from '../hooks/useSEO';

const AboutCheestaPage: React.FC = () => {
    useSEO({
        title: 'درباره چیستا',
        description: 'با چیستا، جامع‌ترین دایرکتوری کسب‌وکارهای ایرانیان استرالیا و ماموریت آن آشنا شوید.',
        canonical: '/about-cheesta-iranain-australia',
    });

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: { xs: 6, md: 10 } }}>
            {/* Hero Section */}
            <Box
                sx={{
                    width: '100%',
                    height: { xs: 150, md: 225 }, // Reduced to half height
                    position: 'relative',
                    overflow: 'hidden',
                    mb: -4 // Reduced negative margin to balance the smaller height
                }}
            >
                <Box
                    component="img"
                    src="/cuneiform-wall-header.jpg"
                    alt="Achaemenid Cuneiform Script Wall"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
                {/* Dark Overlay to make text pop */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            color: 'white',
                            fontSize: { xs: '1.8rem', md: '2.5rem' }, // Scaled down title
                            textShadow: '0 4px 15px rgba(0,0,0,0.6)',
                            textAlign: 'center',
                            px: 2
                        }}
                    >
                        درباره چیستا
                    </Typography>
                </Box>
            </Box>

            <Container maxWidth="md">
                <Paper elevation={4} sx={{ borderRadius: 6, overflow: 'hidden', position: 'relative', zIndex: 2, p: { xs: 3, md: 8 } }}>
                    {/* Meaning of Cheesta */}
                    <Box sx={{ mb: 8, mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, direction: 'rtl' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(40, 84, 161, 0.1)',
                                    color: '#2854a1'
                                }}
                            >
                                <MenuBookIcon />
                            </Box>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
                                معنای لغوی چیستا
                            </Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontSize: { xs: '0.95rem', md: '1rem' },
                                lineHeight: 1.9,
                                color: '#4a5568',
                                textAlign: 'justify',
                                direction: 'rtl'
                            }}
                        >
                            از نظر لغوی چیستا ایزدبانوی ایرانی است که در اوستا به‌ معنای علم، دانش، فرزانگی و معرفت آمده است. همچنین چیستا، دارندهٔ راه‌های خوب و آموزنده و راهنمای راه‌های خوب نیز دانسته می‌شود. چیستی به ‌معنی اندیشیدن و دانستن نیز از این واژه مشتق شده‌است.
                        </Typography>
                    </Box>

                    {/* Statistics */}
                    <Box sx={{ mb: 8 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, direction: 'rtl' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(40, 84, 161, 0.1)',
                                    color: '#2854a1'
                                }}
                            >
                                <TrendingUpIcon />
                            </Box>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
                                نگاهی به آخرین وضعیت جمعیت ایرانیان در استرالیا
                            </Typography>
                        </Stack>
                        <Box sx={{ direction: 'rtl', textAlign: 'right' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.95rem', md: '1rem' },
                                    lineHeight: 1.9,
                                    color: '#4a5568',
                                    textAlign: 'right',
                                    mb: 4
                                }}
                            >
                                بر اساس جدیدترین داده‌های منتشر شده از سوی اداره آمار استرالیا (ABS) و نتایج سرشماری‌های اخیر، جامعه ایرانیان مقیم استرالیا رشد قابل توجهی را تجربه کرده و روند صعودی مهاجرت به این کشور همچنان با شتاب ادامه دارد.
                            </Typography>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2854a1', mb: 2, textAlign: 'right' }}>
                                    آمار کل جمعیت و متولدین ایران
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, direction: 'rtl' }}>
                                        <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2854a1', mt: 1.5, ml: 1 }} />
                                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                            <strong>تعداد کل ایرانی‌تبارها:</strong> طبق سرشماری سال ۲۰۲۱، تعداد افرادی که اصالت و تبار خود را ایرانی اعلام کرده‌اند، به ۸۱,۱۱۹ نفر رسیده است.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, direction: 'rtl' }}>
                                        <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2854a1', mt: 1.5, ml: 1 }} />
                                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                            <strong>جمعیت متولدین ایران:</strong> در حالی که در سال ۲۰۲۱ تعداد ساکنان متولد ایران ۷۰,۸۹۹ نفر ثبت شده بود، تخمین‌های جدیدتر اداره مهاجرت نشان می‌دهد که این رقم تا پایان ژوئن ۲۰۲۳ با جهشی چشمگیر به ۸۵,۸۳۰ نفر افزایش یافته است.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, direction: 'rtl' }}>
                                        <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2854a1', mt: 1.5, ml: 1 }} />
                                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                            <strong>نرخ رشد:</strong> جمعیت متولدین ایران در بازه زمانی ۲۰۱۳ تا ۲۰۲۳، حدود ۶۳.۵ درصد رشد داشته که نشان‌دهنده تداوم جذابیت استرالیا برای مهاجران ایرانی است.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2854a1', mb: 2, textAlign: 'right' }}>
                                    توزیع جغرافیایی و کانون‌های جمعیتی
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, direction: 'rtl' }}>
                                        <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2854a1', mt: 1.5, ml: 1 }} />
                                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                            <strong>تمرکز در سیدنی:</strong> منطقه Sydney Olympic Park همچنان یکی از اصلی‌ترین قطب‌های تجمع ایرانیان است. در سال ۲۰۲۱، حدود ۳.۷ درصد از کل جمعیت این منطقه را متولدین ایران تشکیل می‌دادند. همچنین، ۴.۱ درصد از ساکنان این ناحیه در محیط خانواده به زبان فارسی گفتگو می‌کنند.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, direction: 'rtl' }}>
                                        <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2854a1', mt: 1.5, ml: 1 }} />
                                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                            <strong>جذابیت ملبورن:</strong> حدود ۳۰ درصد از مهاجران ایرانی ایالت ویکتوریا و شهر ملبورن را برای سکونت انتخاب کرده‌اند، هرچند سیدنی کماکان میزبان بزرگترین جامعه ایرانی در استرالیا است.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2854a1', mb: 2, textAlign: 'right' }}>
                                    جایگاه اجتماعی
                                </Typography>
                                <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                    با توجه به این آمار، جامعه ایرانی اکنون به عنوان بیست و چهارمین جامعه بزرگ مهاجر در کشور استرالیا شناخته می‌شود و نقشی پررنگ در ساختار چندفرهنگی این کشور ایفا می‌کند.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Mission & Purpose */}
                    <Box sx={{ mb: 8 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, direction: 'rtl' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(40, 84, 161, 0.1)',
                                    color: '#2854a1'
                                }}
                            >
                                <BusinessCenterIcon />
                            </Box>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
                                رسالت و هدف چیستا
                            </Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontSize: { xs: '0.95rem', md: '1rem' },
                                lineHeight: 1.9,
                                color: '#4a5568',
                                textAlign: 'justify',
                                direction: 'rtl',
                                mb: 3
                            }}
                        >
                            لذا با توجه به رشد جمعیت و مهاجرت هر ساله عده زیادی از هموطنان به شهرهای مختلف استرالیا و افزایش چشمگیر جامعه ایرانیان استرالیا و به طبع آن رشد کسب و کارهای مختلف این جامعه در زمینه های مختلف به عنوان مثال رستوران و کیترینگ، پزشک عمومی و متخصص، دندانپزشک، امور بیمه و مهاجرت، وکیل مهاجرت و ویزا، وام و امور بیمه، خرید و فروش مسکن، ارسال حواله، حمل و نقل، فروشگاه مواد غذایی، آرایشگاه، مکانیک خودرو و غیره. معرفی بیزینس ها و آشنایی هموطنان با آنها از اهمیت خاصی برخوردار است.
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '0.95rem', md: '1rem' },
                                lineHeight: 1.9,
                                color: '#4a5568',
                                textAlign: 'justify',
                                direction: 'rtl'
                            }}
                        >
                            چیستا کمک میکند تا صاحبان کسب و کار بتوانند خدمات و حوزه فعالیت خود را به جامعه ایرانیان استرالیا معرفی نمایند و هموطنان بتوانند نیازمندیهای خود را در بیزینس های ایرانی بیابند تا این جامعه موفق تر و پر رونق تر از پیش در استرالیا رشد نماید.
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 6 }} />

                    {/* Quote Section */}
                    <Box
                        sx={{
                            p: 4,
                            bgcolor: 'rgba(40, 84, 161, 0.05)',
                            borderRadius: 4,
                            position: 'relative',
                            mb: 6,
                            overflow: 'hidden'
                        }}
                    >
                        <FormatQuoteIcon
                            sx={{
                                position: 'absolute',
                                top: -20,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: '3rem',
                                color: '#2854a1',
                                bgcolor: '#ffffff',
                                borderRadius: '50%',
                                p: 0.5,
                                zIndex: 2
                            }}
                        />

                        <Grid container spacing={3} alignItems="center" sx={{ direction: 'rtl' }}>
                            <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box
                                    component="img"
                                    src="cheesta-cyrus-cylinder.jpg"
                                    alt="Cyrus Cylinder - First Charter of Human Rights"
                                    sx={{
                                        width: 140,
                                        height: 140,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '4px solid #ffffff',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 9 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#2d3748',
                                        lineHeight: 1.8,
                                        direction: 'rtl',
                                        textAlign: 'justify',
                                        mb: 1
                                    }}
                                >
                                    همیشه با هم یکدل و صمیمی بمانید تا اتحادتان موید و پایدار بماند و بدانید آفتاب به گیاهی حرارت می دهد که سر از خاک بیرون آورده باشد.
                                </Typography>
                                <Typography variant="subtitle1" sx={{ color: '#2854a1', fontWeight: 700, textAlign: 'left' }}>
                                    — کوروش بزرگ
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            color: '#718096',
                            fontStyle: 'italic'
                        }}
                    >
                        .به امید موفقیت و رشد و پیشرفت روزافزون ایرانیان در همه جای این کره خاکی
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default AboutCheestaPage;
